import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace-store';
import Loader from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { getDiscordChannelIcon } from '@/lib/channelIcons';
import { ticketApi } from '@/lib/api/ticket/ticketApi';
import { TicketChatInterface } from '@/components/tickets/TicketChatInterface';
import { TicketMetaPanel } from '@/components/tickets/TicketMetaPanel';
import { TicketActivityLog } from '@/components/tickets/TicketActivityLog';
import { toast } from 'sonner';
import type {
  ConversationEvent,
  Ticket as TicketType,
  TicketThread,
  TicketResponse
} from 'dico-types';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

// ─── sub-components ──────────────────────────────────────────────────────────

function MessageBubble({ event, isOriginal }: { event: ConversationEvent; isOriginal?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div className="border-border bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs font-semibold uppercase">
        {(event.author.name ?? event.author.id).slice(0, 1)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-medium">
            {event.author.name ?? event.author.id}
          </span>
          <span
            className="text-muted-foreground text-xs"
            title={formatAbsoluteTime(event.timestamp)}
          >
            {formatRelativeTime(event.timestamp)}
          </span>

          {event.permalink && (
            <a
              href={event.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs transition-colors"
            >
              <MonoIcon type="ExternalLink" />
              View original
            </a>
          )}
        </div>
        <p className="text-foreground mt-1 whitespace-pre-wrap text-sm leading-relaxed">
          {event.content}
        </p>
      </div>
    </div>
  );
}

function ResponseBubble({ response }: { response: TicketResponse }) {
  return (
    <div className="flex items-start gap-3">
      <div className="border-border bg-foreground text-background flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold">
        D
      </div>

      <div className="border-border bg-card min-w-0 flex-1 rounded-lg border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-medium">Dico</span>
          {response.publishedAt && (
            <span
              className="text-muted-foreground text-xs"
              title={formatAbsoluteTime(response.publishedAt)}
            >
              {formatRelativeTime(response.publishedAt)}
            </span>
          )}
          <span className="bg-muted text-muted-foreground flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
            <MonoIcon type="CheckCircle" />
            Published
          </span>
          {response.permalink && (
            <a
              href={response.permalink}
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs transition-colors"
            >
              <MonoIcon type="ExternalLink" />
              View in channel
            </a>
          )}
        </div>
        <p className="text-foreground mt-1 whitespace-pre-wrap text-sm leading-relaxed">
          {response.content}
        </p>
      </div>
    </div>
  );
}

function DraftResponseBubble({
  response,
  workspaceId,
  ticketId,
  onPublished,
  onDeleted
}: {
  response: TicketResponse;
  workspaceId: string;
  ticketId: string;
  onPublished?: (response: TicketResponse) => void;
  onDeleted?: (responseId: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(response.content);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (editedContent === response.content) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await ticketApi.updateTicketResponse(workspaceId, ticketId, response.id, {
        content: editedContent
      });
      response.content = editedContent;
      setIsEditing(false);
      toast.success('Draft updated');
    } catch (err) {
      toast.error('Failed to update draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const { data: published } = await ticketApi.publishTicketResponse(
        workspaceId,
        ticketId,
        response.id
      );
      if (published) {
        onPublished?.(published);
        toast.success('Response published');
      }
    } catch (err) {
      toast.error('Failed to publish response');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this draft?')) return;

    setIsDeleting(true);
    try {
      await ticketApi.deleteTicketResponse(workspaceId, ticketId, response.id);
      onDeleted?.(response.id);
      toast.success('Draft deleted');
    } catch (err) {
      toast.error('Failed to delete draft');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="border-border bg-blue-500 text-white flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold">
        D
      </div>

      <div className="bg-blue-50 dark:bg-blue-950/20 min-w-0 flex-1 rounded-lg border border-blue-200 dark:border-blue-800 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-medium">Dico</span>
          <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
            <MonoIcon type="Edit" />
            Draft
          </span>
        </div>

        {isEditing ? (
          <div className="mt-2 flex flex-col gap-2">
            <textarea
              className="bg-background text-foreground border-border min-h-[100px] w-full resize-none rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              disabled={isSaving}
            />
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isSaving} size="sm">
                {isSaving ? (
                  <>
                    <Loader className="h-3 w-3 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <MonoIcon type="Save" />
                    Save
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setEditedContent(response.content);
                  setIsEditing(false);
                }}
                disabled={isSaving}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-foreground mt-2 whitespace-pre-wrap text-sm leading-relaxed">
              {response.content}
            </p>
            <div className="mt-3 flex gap-2">
              <Button onClick={handlePublish} disabled={isPublishing || isDeleting} size="sm">
                {isPublishing ? (
                  <>
                    <Loader className="h-3 w-3 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <MonoIcon type="Send" />
                    Publish
                  </>
                )}
              </Button>
              <Button
                onClick={() => setIsEditing(true)}
                disabled={isPublishing || isDeleting}
                variant="outline"
                size="sm"
              >
                <MonoIcon type="Edit" />
                Edit
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isPublishing || isDeleting}
                variant="outline"
                size="sm"
              >
                {isDeleting ? (
                  <>
                    <Loader className="h-3 w-3 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <MonoIcon type="Trash" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── thread card ─────────────────────────────────────────────────────────────

function ThreadCard({
  thread,
  index,
  allReplies,
  allResponses,
  conversationIds,
  workspaceId,
  ticketId,
  onResponsePublished,
  onDraftCreated
}: {
  thread: TicketThread;
  index: number;
  allReplies: ConversationEvent[];
  allResponses: TicketResponse[];
  conversationIds: Set<string>;
  workspaceId: string;
  ticketId: string;
  onResponsePublished: (response: TicketResponse) => void;
  onDraftCreated: (response: TicketResponse) => void;
}) {
  const [localResponses, setLocalResponses] = useState<TicketResponse[]>([]);

  useEffect(() => {
    setLocalResponses([]);
  }, [allResponses]);

  const threadIdNorm = thread.threadId ?? null;

  // All conversation events that belong to this thread
  const ownConversations: ConversationEvent[] = thread.conversations ?? [];

  // Other-user replies that belong to this thread (same channel + thread root)
  const matchingReplies = allReplies.filter((e) => {
    if (e.channelId !== thread.channelId) return false;
    // threadReplies have e.threadId pointing to the root message ts
    const replyThread = e.threadId ?? null;
    return replyThread === threadIdNorm || e.channelId === thread.channelId;
  });

  // Deduplicate by id (ownConversations may already include some)
  const ownIds = new Set(ownConversations.map((e) => e.id));
  const extraReplies = matchingReplies.filter((e) => !ownIds.has(e.id));

  // Combine allResponses with localResponses, removing duplicates
  const combinedResponses = [
    ...allResponses,
    ...localResponses.filter((lr) => !allResponses.some((ar) => ar.id === lr.id))
  ];

  // Dico's published responses for this thread
  const publishedResponses = combinedResponses.filter(
    (r) =>
      r.status === 'published' &&
      r.channelId === thread.channelId &&
      (r.threadId ?? null) === threadIdNorm
  );

  // Draft responses for this thread
  const draftResponses = combinedResponses.filter(
    (r) =>
      r.status === 'draft' &&
      r.channelId === thread.channelId &&
      (r.threadId ?? null) === threadIdNorm
  );

  // Merge messages + responses, sorted by time
  type TimelineItem =
    | { kind: 'message'; event: ConversationEvent }
    | { kind: 'response'; response: TicketResponse }
    | { kind: 'draft'; response: TicketResponse };

  const items: TimelineItem[] = [
    ...ownConversations.map((e) => ({ kind: 'message' as const, event: e })),
    ...extraReplies.map((e) => ({ kind: 'message' as const, event: e })),
    ...publishedResponses.map((r) => ({ kind: 'response' as const, response: r })),
    ...draftResponses.map((r) => ({ kind: 'draft' as const, response: r }))
  ].sort((a, b) => {
    const ta =
      a.kind === 'message'
        ? new Date(a.event.timestamp).getTime()
        : new Date(a.response.publishedAt ?? a.response.createdAt).getTime();
    const tb =
      b.kind === 'message'
        ? new Date(b.event.timestamp).getTime()
        : new Date(b.response.publishedAt ?? b.response.createdAt).getTime();
    return ta - tb;
  });

  const handleDraftPublished = (response: TicketResponse) => {
    // Remove from local responses and add to global
    setLocalResponses((prev) => prev.filter((r) => r.id !== response.id));
    onResponsePublished(response);
  };

  const handleDraftDeleted = (responseId: string) => {
    // Remove from local responses
    setLocalResponses((prev) => prev.filter((r) => r.id !== responseId));
  };

  const handleNewDraft = (response: TicketResponse) => {
    // Add to local responses
    setLocalResponses((prev) => [...prev, response]);
    onDraftCreated(response);
  };

  const channelLabel =
    thread.channelId.length > 20 ? `${thread.channelId.slice(0, 20)}…` : thread.channelId;

  const threadTitleRaw = thread.threadName?.trim();
  const threadHeaderTitle = threadTitleRaw
    ? threadTitleRaw.length > 72
      ? `${threadTitleRaw.slice(0, 69)}…`
      : threadTitleRaw
    : channelLabel;

  return (
    <div className="border-border rounded-md border">
      {/* Thread header */}
      <div className="border-border flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <MonoIcon
            type={
              thread.source === 'discord'
                ? 'Discord'
                : thread.source === 'slack'
                  ? 'Slack'
                  : 'Circle'
            }
          />
          <div className="flex min-w-0 items-center gap-1">
            <MonoIcon
              type={
                thread.source === 'discord'
                  ? getDiscordChannelIcon(thread.channelType ?? 0)
                  : 'Hash'
              }
              className="text-muted-foreground shrink-0"
            />
            <span
              className="text-foreground min-w-0 truncate text-sm font-medium"
              title={threadTitleRaw || thread.channelId}
            >
              {threadHeaderTitle}
            </span>
            <span className="text-muted-foreground text-xs">{thread.messageCount}</span>
          </div>
        </div>

        <div className="text-muted-foreground flex items-center gap-3 text-xs">
          <span title={formatAbsoluteTime(thread.firstMessageAt)}>
            {formatRelativeTime(thread.firstMessageAt)}
          </span>
        </div>
      </div>

      {/* Messages + responses */}
      <div className="bg-card shadow-xs m-1 mt-0 flex flex-col gap-5 rounded-md border px-4 py-4">
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">No messages in this thread.</p>
        ) : (
          items.map((item, i) => {
            if (item.kind === 'message') {
              return (
                <MessageBubble
                  key={item.event.id ?? i}
                  event={item.event}
                  isOriginal={conversationIds.has(item.event.id)}
                />
              );
            }
            if (item.kind === 'draft') {
              return (
                <DraftResponseBubble
                  key={item.response.id}
                  response={item.response}
                  workspaceId={workspaceId}
                  ticketId={ticketId}
                  onPublished={handleDraftPublished}
                  onDeleted={handleDraftDeleted}
                />
              );
            }
            return <ResponseBubble key={item.response.id} response={item.response} />;
          })
        )}
      </div>

      {/* Reply input */}
      <div className="bg-card shadow-xs m-1 rounded-md border p-2">
        <TicketChatInterface
          workspaceId={workspaceId}
          ticketId={ticketId}
          thread={thread}
          onDraftCreated={handleNewDraft}
          onResponsePublished={onResponsePublished}
        />
      </div>
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export function TicketDetailPage() {
  const { id: workspaceId, ticketId } = useParams<{ id: string; ticketId: string }>();
  const navigate = useNavigate();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId);

  const [ticket, setTicket] = useState<TicketType | null>(null);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  const loadTicket = useCallback(async () => {
    if (!workspaceId || !ticketId) return;
    setLoadState('loading');
    try {
      const res = await ticketApi.getTicket(workspaceId, ticketId);
      setTicket(res.data ?? null);
      setLoadState('loaded');
    } catch {
      setLoadState('error');
    }
  }, [workspaceId, ticketId]);

  /** Merge a partial update into the local ticket state without a re-fetch. */
  const patchTicket = useCallback((patch: Partial<TicketType>) => {
    setTicket((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  /** Append a newly published response to the local ticket state. */
  const handleResponsePublished = useCallback((response: TicketResponse) => {
    setTicket((prev) => {
      if (!prev) return prev;
      const existing = prev.responses ?? [];
      // Avoid duplicates if the same response somehow arrives twice
      if (existing.some((r) => r.id === response.id)) return prev;
      return { ...prev, responses: [...existing, response] };
    });
  }, []);

  /** Called when a draft is created (handled locally in ThreadCard). */
  const handleDraftCreated = useCallback((response: TicketResponse) => {
    // Drafts are handled locally in ThreadCard, nothing to do here
  }, []);

  useEffect(() => {
    loadTicket();
  }, []);

  useEffect(() => {
    if (loadState !== 'error') return;
    toast.error('Failed to load ticket. Check your connection and try again.');
  }, [loadState]);

  if (!workspace) {
    return (
      <div className="text-muted-foreground flex min-h-screen items-center justify-center">
        Workspace not found.
      </div>
    );
  }

  const threads = ticket?.threads ?? [];
  const allResponses = ticket?.responses ?? [];
  const allReplies = ticket?.threadReplies ?? [];
  const conversationIds = new Set(ticket?.conversationIds ?? []);

  return (
    <main className="flex min-h-full flex-1 flex-col">
      {/* ── header ── */}
      <div className="border-border flex shrink-0 items-center gap-3 border-b p-3 px-4">
        <button
          onClick={() => navigate(`/workspace/${workspaceId}/tickets`)}
          className="text-muted-foreground hover:text-foreground rounded-md p-1 transition-colors"
          aria-label="Back"
        >
          <MonoIcon type="ArrowLeft" className="h-4 w-4" />
        </button>
      </div>

      {/* ── content ── */}
      <div className="w-3xl mx-auto min-h-full flex-1 px-6 py-8 xl:border-x">
        {loadState === 'loading' && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader className="h-4 w-4" />
            <span>Loading ticket…</span>
          </div>
        )}

        {loadState === 'loaded' && !ticket && (
          <div className="border-border rounded-lg border border-dashed p-10 text-center">
            <MonoIcon type="Ticket" className="text-muted-foreground mx-auto mb-3" />
            <p className="text-foreground text-sm font-medium">Ticket not found</p>
          </div>
        )}

        {loadState === 'loaded' && ticket && (
          <div className="flex flex-col gap-6">
            {/* ── ticket header ── */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">{ticket.title}</h1>
                  <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2 text-sm">
                    <span
                      className="flex items-center gap-1"
                      title={formatAbsoluteTime(ticket.createdAt)}
                    >
                      <MonoIcon type="Clock" />
                      Opened {formatRelativeTime(ticket.createdAt)}
                    </span>
                    {threads.length > 0 && (
                      <>
                        <span className="text-muted-foreground/50">·</span>
                        <span className="flex items-center gap-1">
                          <MonoIcon type="Reply" />
                          {threads.length} thread{threads.length !== 1 ? 's' : ''}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {ticket.summary && (
              <p className="text-muted-foreground text-sm leading-relaxed">{ticket.summary}</p>
            )}

            {/* ── properties ── */}
            <TicketMetaPanel
              workspaceId={workspaceId}
              ticket={ticket}
              onTicketPatch={patchTicket}
            />

            {/* ── threads ── */}
            {threads.length > 0 ? (
              <div className="flex flex-col gap-4">
                {threads.map((thread, i) => (
                  <ThreadCard
                    key={`${thread.channelId}:${thread.threadId ?? 'root'}`}
                    thread={thread}
                    index={i}
                    allReplies={allReplies}
                    allResponses={allResponses}
                    conversationIds={conversationIds}
                    workspaceId={workspaceId}
                    ticketId={ticketId}
                    onResponsePublished={handleResponsePublished}
                    onDraftCreated={handleDraftCreated}
                  />
                ))}
              </div>
            ) : (
              ticket.description && (
                <div>
                  <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
                    Original Message
                  </p>
                  <div className="flex items-start gap-3">
                    <div className="border-border bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold uppercase">
                      ?
                    </div>
                    <p className="text-foreground mt-0.5 whitespace-pre-wrap text-sm leading-relaxed">
                      {ticket.description}
                    </p>
                  </div>
                </div>
              )
            )}

            {/* ── activity history ── */}
            <div>
              <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
                Activity History
              </p>
              <TicketActivityLog workspaceId={workspaceId!} ticketId={ticketId!} />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
